import { pacedTimeline, TEMPO } from './tempo';

/*
 * The story API the longer vignettes are told in: a story is a list of
 * beats, each a named moment that fires a set time after the one before it,
 * grouped into units (one thing the product does: complete a passport,
 * release it, compose its public page), and units chain into a story. A
 * scene reads where the story is from the playback's step, by beat name,
 * and renders that; the beats are the whole script.
 *
 *   const COMPLETE = unit('complete', { page: 'passport', beats: [
 *       beat('point-row', 1200),                     // 1.2s of script in (0.9s played), the cursor sets off
 *       beat('open-drawer', 800, { needs: 'drawer' }),
 *       ...typing('type-value', '75.2', { first: 550, perChar: 60 }),
 *   ] });
 *   const story = chain([COMPLETE, RELEASE]);
 *   usePlayback({ timeline: story.timeline, ... });
 *   const at = storyAt(story, step);
 *   at.reached('complete:open-drawer');               // true once it has fired
 *
 * Step 0 is the opening state, before any beat; step N means the Nth beat
 * has fired; the last step is the end state. `after` is how long the story
 * waits, from the beat before it (or from the opening), before a beat fires,
 * in script ms: the story plays at TEMPO of its script (see tempo.js), so a
 * beat written `after` 1200 fires 900ms after the one before it. A beat's
 * own animation must fit inside the next beat's played `after`.
 *
 * A beat may say how long what it sets off takes to finish (`settles`: the
 * app's own animation, the drawer rising, a dip, a chart drawing). The beat
 * after it then never fires sooner, whatever the tempo (its played `after`
 * is floored there), and on the last beat the story's rest on its end
 * starts counting once it is over (see useStory). Mind the units: `after`
 * is script ms (paced by the knob), `settles` played ms (never paced).
 */

/** One moment of a story: `id`, fired `after` script ms after the beat before it. */
export function beat(id, after, extra = {}) {
    return { id, after, ...extra };
}

/**
 * A unit: the beats of one thing the product does, on one `page` of the
 * window (units on the same page share it). Beat ids are prefixed with the
 * unit's name (`complete:open-drawer`), so a chained story names each beat
 * once. `beats` may nest arrays (typing() returns one).
 *
 * A unit that goes to another page and back (an import opened from the
 * passport, run on the import's page, then back on the passport) names the
 * page on the beats that happen there: `beat('run', 900, { page: 'import' })`.
 * Every beat carries its page (the unit's unless it names its own).
 *
 * `requires` names the units whose end this one opens on (a release needs
 * the cells imported first): chain() refuses, in development, a story that
 * plays it without them before it or `settled`.
 */
export function unit(name, { page, beats, requires = [] }) {
    return {
        name,
        page,
        requires,
        beats: beats.flat().map((entry) => ({
            ...entry,
            id: `${name}:${entry.id}`,
            unit: name,
            page: entry.page ?? page,
            ...(entry.typing ? { typing: `${name}:${entry.typing}` } : {}),
        })),
    };
}

/** Every page a story shows, in the order it first shows them. */
export function storyPages(story) {
    return [...new Set([story.units[0]?.page, ...story.beats.map((entry) => entry.page)].filter(Boolean))];
}

/**
 * Units in the order they play, as one story: `beats`, the playback
 * `timeline` (each beat's `after` at `tempo`, TEMPO unless given, and never
 * less than what the beat before it `settles`: what usePlayback holds on
 * the step before it), and each beat's step.
 * `settled` names units that are over before this story starts (a story
 * that picks up after them opens where they ended: their beats all count
 * as reached). `settles` is the last beat's (0 if it says none).
 */
export function chain(units, { settled = [], tempo = TEMPO } = {}) {
    const settledNames = settled.map((entry) => (typeof entry === 'string' ? entry : entry.name));
    if (import.meta.env.DEV) {
        // A unit's name prefixes its beats' ids and is what `requires` and
        // `settled` name: two units (from two stories, say) may not share one.
        const named = new Set();
        units.forEach((entry) => {
            if (named.has(entry.name)) {
                throw new Error(`Two story units are named "${entry.name}": a unit's name prefixes its beats' ids and must be unique in a story.`);
            }
            named.add(entry.name);
        });
        const ids = new Set();
        units.flatMap((entry) => entry.beats).forEach((entry) => {
            if (ids.has(entry.id)) {
                throw new Error(`Two beats of the story are "${entry.id}": a beat's id must be unique in a story.`);
            }
            ids.add(entry.id);
        });
        units.forEach((entry, position) => {
            const before = new Set([...settledNames, ...units.slice(0, position).map((other) => other.name)]);
            const missing = (entry.requires ?? []).filter((name) => !before.has(name));
            if (missing.length > 0) {
                const names = missing.map((name) => `"${name}"`).join(' and ');
                throw new Error(
                    `The story unit "${entry.name}" opens where ${names} left the page: chain ${missing.length > 1 ? 'them' : 'it'} before it, `
                    + `or name ${missing.length > 1 ? 'them' : 'it'} in \`settled\` (chain([...], { settled: [...] })).`,
                );
            }
        });
    }
    const beats = units.flatMap((entry) => entry.beats);
    const index = new Map(beats.map((entry, position) => [entry.id, position]));
    const typings = new Map();
    beats.forEach((entry, position) => {
        if (!entry.typing) return;
        if (!typings.has(entry.typing)) typings.set(entry.typing, []);
        typings.get(entry.typing).push(position);
    });

    return {
        units,
        beats,
        timeline: pacedTimeline(beats.map((entry) => entry.after), tempo, beats.map((_, position) => beats[position - 1]?.settles ?? 0)),
        index,
        typings,
        settled: new Set(settledNames),
        settles: beats.at(-1)?.settles ?? 0,
    };
}

/**
 * When `id` fires, in played ms from the start of the story (the sum of
 * the timeline up to and including its own beat). For tests and for tuning.
 */
export function firesAt(story, id) {
    const position = story.index.get(id);
    if (position === undefined) throw new Error(`No beat "${id}" in this story`);

    return story.timeline.slice(0, position + 1).reduce((total, after) => total + after, 0);
}

/** The whole story's length, from its opening to its last beat, in played ms. */
export function storyLength(story) {
    return story.timeline.reduce((total, after) => total + after, 0);
}

/**
 * Where `story` is at playback `step`:
 *
 * - `reached(id)`: the beat has fired (a settled unit's always have; a beat
 *   this story does not have never has).
 * - `typed(id, text)`: how much of `text` a typing() run has typed so far.
 * - `next`: the beat about to fire (null at the end); its `needs` names a
 *   part the story must hold for (see useStory).
 * - `page`: the page the window shows: that of the beat that fired last
 *   (its unit's, unless it names its own), the first unit's before any.
 * - `last`: the beat that fired last (null at the opening).
 */
export function storyAt(story, step) {
    const fired = Math.max(0, Math.min(step, story.beats.length));
    const last = fired > 0 ? story.beats[fired - 1] : null;

    const reached = (id) => {
        const [unitName] = id.split(':');
        if (story.settled.has(unitName)) return true;
        const position = story.index.get(id);

        return position !== undefined && position < fired;
    };

    return {
        step: fired,
        last,
        next: story.beats[fired] ?? null,
        page: last ? last.page : story.units[0]?.page,
        reached,
        typed(id, text) {
            const [unitName] = id.split(':');
            const characters = [...text];
            if (story.settled.has(unitName)) return text;
            const positions = story.typings.get(id) ?? [];

            return characters.slice(0, positions.filter((position) => position < fired).length).join('');
        },
    };
}
